import React from 'react';
import { Check, GitBranch, Plus, Trash2 } from 'lucide-react';
import { Condition, FieldType, Operator } from './types';
import { iconButton, inputClass, subtleButton } from './ui';

const operators: { value: Operator; label: string; needsValue: boolean }[] = [
  { value: 'present', label: 'Field is present (any value)', needsValue: false },
  { value: 'visible', label: 'Field is visible', needsValue: false },
  { value: 'hidden', label: 'Field is hidden', needsValue: false },
  { value: 'changed', label: 'Field value changes', needsValue: false },
  { value: 'equals', label: 'Equals', needsValue: true },
  { value: 'not_equals', label: 'Does not equal', needsValue: true },
  { value: 'in', label: 'Is one of', needsValue: true },
  { value: 'not_in', label: 'Is not one of', needsValue: true },
  { value: 'contains', label: 'Contains', needsValue: true },
  { value: 'not_contains', label: 'Does not contain', needsValue: true },
  { value: 'empty', label: 'Is empty', needsValue: false },
  { value: 'not_empty', label: 'Is not empty', needsValue: false },
  { value: 'gt', label: 'Greater than', needsValue: true },
  { value: 'gte', label: 'Greater than or equal', needsValue: true },
  { value: 'lt', label: 'Less than', needsValue: true },
  { value: 'lte', label: 'Less than or equal', needsValue: true },
];

type FieldRef = { key: string; label: string; type: FieldType };
type GroupKey = 'all' | 'any' | 'none' | 'one' | 'not';

const groupOptions: { value: GroupKey; label: string; description: string; min: number; max?: number }[] = [
  { value: 'all', label: 'All / AND', description: 'Every condition must match.', min: 1 },
  { value: 'any', label: 'Any / OR', description: 'At least one condition must match.', min: 1 },
  { value: 'none', label: 'None / NOR', description: 'No condition may match.', min: 1 },
  { value: 'one', label: 'Exactly one / XOR', description: 'Exactly one condition must match.', min: 1 },
  { value: 'not', label: 'Not / NOT', description: 'Invert the result of the nested condition.', min: 1, max: 1 },
];

const blank = (fields: FieldRef[]): Condition => ({
  field: fields[0]?.key || '',
  operator: 'equals',
  value: '',
});

function groupKey(condition?: Condition): GroupKey | undefined {
  if (!condition) return undefined;
  return (['all', 'any', 'none', 'one', 'not'] as GroupKey[]).find((key) => Array.isArray(condition[key])) as GroupKey | undefined;
}

function groupChildren(condition: Condition, key: GroupKey): Condition[] {
  return Array.isArray(condition[key]) ? (condition[key] as Condition[]) : [];
}

export default function ConditionEditor({
  value,
  fields,
  onChange,
}: {
  value?: Condition;
  fields: FieldRef[];
  onChange: (v?: Condition) => void;
}) {
  const c = value || blank(fields);
  const mode = groupKey(c);

  if (mode) {
    const children = groupChildren(c, mode);
    const meta = groupOptions.find((x) => x.value === mode)!;
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-2.5">
        <div className="flex items-start gap-2">
          <GitBranch className="mt-2 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <div className="min-w-0 flex-1">
            <select
              className={inputClass()}
              value={mode}
              onChange={(e) => {
                const next = e.target.value as GroupKey;
                const nextChildren = next === 'not' ? children.slice(0, 1) : children;
                onChange({ [next]: nextChildren } as Condition);
              }}
              aria-label="Condition group operator"
            >
              {groupOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <p className="mt-1 text-[10px] text-slate-400">{meta.description}</p>
          </div>
          <button type="button" className={iconButton} aria-label="Remove condition group" onClick={() => onChange(undefined)}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="mt-2 space-y-2 border-l-2 border-slate-200 pl-3">
          {children.map((child, i) => (
            <ConditionEditor
              key={`${i}-${child.field || groupKey(child) || 'condition'}`}
              value={child}
              fields={fields}
              onChange={(v) => {
                const next = [...children];
                if (v) next[i] = v;
                else next.splice(i, 1);
                if (!next.length) onChange(undefined);
                else onChange({ [mode]: next } as Condition);
              }}
            />
          ))}
        </div>

        {meta.max !== 1 && (
          <button
            type="button"
            className={`${subtleButton} mt-2`}
            onClick={() => onChange({ [mode]: [...children, blank(fields)] } as Condition)}
          >
            <Plus className="h-3.5 w-3.5" /> Add condition
          </button>
        )}
        <button
          type="button"
          className={`${subtleButton} mt-2 ml-2`}
          onClick={() => onChange({ [mode]: [...children, { all: [blank(fields)] }] } as Condition)}
          disabled={meta.max === 1}
        >
          <GitBranch className="h-3.5 w-3.5" /> Add group
        </button>
      </div>
    );
  }

  const op = operators.find((x) => x.value === c.operator) || operators[0];
  const ref = fields.find((f) => f.key === c.field);

  const setValue = (raw: string) => {
    let parsed: unknown = raw;
    if (['in', 'not_in'].includes(c.operator || '')) {
      try { parsed = JSON.parse(raw); }
      catch { parsed = raw.split(',').map((x) => x.trim()).filter(Boolean); }
    } else if (ref?.type === 'number' || ['gt', 'gte', 'lt', 'lte'].includes(c.operator || '')) {
      parsed = raw === '' ? '' : Number(raw);
    } else if (ref?.type === 'checkbox') {
      parsed = raw === 'true';
    }
    onChange({ ...c, value: parsed });
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_32px] items-center gap-1.5">
      <select className={inputClass()} value={c.field || ''} onChange={(e) => onChange({ ...c, field: e.target.value })} aria-label="Condition field">
        {fields.map((f) => <option key={f.key} value={f.key}>{f.label || f.key} · {f.key}</option>)}
      </select>
      <select
        className={inputClass()}
        value={c.operator || 'equals'}
        onChange={(e) => {
          const next = e.target.value as Operator;
          onChange({ ...c, operator: next, value: ['present', 'visible', 'hidden', 'changed', 'empty', 'not_empty'].includes(next) ? undefined : c.value });
        }}
        aria-label="Condition operator"
      >
        {operators.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {op.needsValue ? (
        <input
          className={inputClass()}
          value={typeof c.value === 'string' || typeof c.value === 'number' ? String(c.value) : JSON.stringify(c.value ?? '')}
          onChange={(e) => setValue(e.target.value)}
          placeholder={['in', 'not_in'].includes(c.operator || '') ? 'a,b or ["a","b"]' : 'Value'}
          aria-label="Condition value"
        />
      ) : (
        <div className="flex h-9 items-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-2.5 text-[11px] text-slate-400">
          <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-500" /> No value required
        </div>
      )}
      <button type="button" className={iconButton} aria-label="Remove condition" onClick={() => onChange(undefined)}>
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
